import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Link } from 'react-router-dom';

import { PageHead, Stat, StatBand } from '../components/bits.tsx';
import { Alert, Badge, Button, Panel, TextField, useToast } from '../components/ui/index.tsx';
import { api } from '../lib/api.ts';
import { describeError } from '../lib/errors.ts';
import { discountedPrice, formatMoney } from '../lib/money.ts';
import { useSession } from '../state/session.tsx';

interface Overview {
  monthlyRevenue: { currency: string; amountMinor: number }[];
  companies: number;
  payingCount: number;
  trialingCount: number;
  newThisMonth: number;
  byPlan: Record<string, number>;
  openRequests: number;
  expiringSoon: { businessId: string; name: string; endsAt: string; plan: string }[];
  needsAttention: { businessId: string; name: string; status: string; plan: string }[];
}

interface Company {
  id: string;
  name: string;
  plan: string;
  planName: string;
  status: string;
  trialEndsAt: string | null;
  members: number;
  issuedThisMonth: number;
  joinedAt: string;
  currency: string;
  discountBp: number;
  payableMinor: number;
}

interface PlanRequest {
  id: string;
  businessId: string;
  businessName: string;
  requestedPlan: string;
  requestedPlanName: string;
  requestedBy: string | null;
  requestedEmail: string | null;
  requestedAt: string;
  currentPlan: string;
  currentPlanName: string;
  requestedPriceMinor: number;
  currency: string;
  settled: boolean;
}

/**
 * Running Vela, rather than using it.
 *
 * Only plan requests live here. Everything a support question might tempt you
 * to add — reading someone's invoices to "check something" — is deliberately
 * absent, because approving a plan has never needed it and a tool that can
 * read every tenant's books is the worst breach this product could have.
 */
export function Admin() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [error, setError] = useState<string | null>(null);

  const { data: overview } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<Overview>('/api/admin/overview'),
    enabled: Boolean(user?.isPlatformAdmin),
  });

  const { data: companyData } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => api.get<{ companies: Company[] }>('/api/admin/companies'),
    enabled: Boolean(user?.isPlatformAdmin),
  });

  const [writingTo, setWritingTo] = useState<string | null>(null);
  const [note, setNote] = useState({ subject: '', message: '' });

  const notify_ = useMutation({
    mutationFn: (input: { businessId: string; subject: string; message: string }) =>
      api.post<{ delivered: number; recipients: number }>('/api/admin/notify', input),
    onSuccess: (result) => {
      setError(null);
      setWritingTo(null);
      setNote({ subject: '', message: '' });
      notify(
        result.delivered === result.recipients
          ? `Sent to ${result.delivered}`
          : `Sent to ${result.delivered} of ${result.recipients} — the rest bounced`,
        'success',
      );
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'plan-requests'],
    queryFn: () => api.get<{ requests: PlanRequest[] }>('/api/admin/plan-requests'),
    enabled: Boolean(user?.isPlatformAdmin),
  });

  // Keyed by business so two rows on screen do not share one input.
  const [discounts, setDiscounts] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const setPlan = useMutation({
    mutationFn: (input: { businessId: string; plan: string; discountPercent?: number }) =>
      api.post<{ planName: string; priceMinor: number; currency: string; discountBp: number }>(
        '/api/admin/plan',
        input,
      ),
    onSuccess: (result) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notify(
        `${result.planName} at ${formatMoney(result.priceMinor, result.currency)}${
          result.discountBp > 0 ? ` (${result.discountBp / 100}% off)` : ''
        }`,
        'success',
      );
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const decline = useMutation({
    mutationFn: (input: { businessId: string; plan: string; reason?: string }) =>
      api.post<{ declined: boolean }>('/api/admin/plan-decline', input),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notify('Declined, and they have been told', 'success');
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (!user?.isPlatformAdmin) {
    return (
      <div className="page">
        <PageHead title="Not found" lede="There is nothing at this address." />
      </div>
    );
  }

  const days = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));

  const requests = data?.requests ?? [];
  const outstanding = requests.filter((entry) => !entry.settled);
  const settled = requests.filter((entry) => entry.settled);

  const row = (entry: PlanRequest) => {
    const raw = discounts[entry.businessId] ?? '';
    const percent = raw.trim() === '' ? 0 : Number(raw);
    const valid = Number.isFinite(percent) && percent >= 0 && percent <= 100;
    // Basis points, converted the moment the number leaves the input, so no
    // float percentage ever reaches a price.
    const discountBp = valid ? Math.round(percent * 100) : 0;
    const payable = discountedPrice(entry.requestedPriceMinor, discountBp);
    const busy =
      (setPlan.isPending && setPlan.variables?.businessId === entry.businessId) ||
      (decline.isPending && decline.variables?.businessId === entry.businessId);

    return (
      <div key={entry.id} className="admin-request">
        <div className="row">
          <span className="strong">{entry.businessName}</span>
          <Badge tone={entry.settled ? 'neutral' : 'accent'}>
            {entry.currentPlanName} → {entry.requestedPlanName}
          </Badge>
          {entry.settled && <Badge tone="neutral">done</Badge>}
        </div>

        <p className="muted" style={{ fontSize: 'var(--text-xs)', margin: 0 }}>
          {entry.requestedBy ?? 'Someone'}
          {entry.requestedEmail ? ` · ${entry.requestedEmail}` : ''} ·{' '}
          {new Date(entry.requestedAt).toLocaleString()}
        </p>

        {!entry.settled && (
          <div className="admin-decision">
            <TextField
              label="Discount %"
              value={raw}
              inputMode="decimal"
              placeholder="0"
              onChange={(event) =>
                setDiscounts((current) => ({ ...current, [entry.businessId]: event.target.value }))
              }
              hint={
                valid
                  ? discountBp > 0
                    ? `${formatMoney(entry.requestedPriceMinor, entry.currency)} → ${formatMoney(payable, entry.currency)} a month`
                    : `${formatMoney(entry.requestedPriceMinor, entry.currency)} a month`
                  : 'Enter a number between 0 and 100'
              }
            />
            <TextField
              label="If declining, why"
              value={reasons[entry.businessId] ?? ''}
              placeholder="Shown to them"
              onChange={(event) =>
                setReasons((current) => ({ ...current, [entry.businessId]: event.target.value }))
              }
            />
            <div className="row row--end">
              <Button
                size="sm"
                variant="danger"
                disabled={busy}
                onClick={() =>
                  decline.mutate({
                    businessId: entry.businessId,
                    plan: entry.requestedPlan,
                    reason: reasons[entry.businessId]?.trim() || undefined,
                  })
                }
              >
                Decline
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!valid || busy}
                loading={setPlan.isPending && setPlan.variables?.businessId === entry.businessId}
                onClick={() =>
                  setPlan.mutate({
                    businessId: entry.businessId,
                    plan: entry.requestedPlan,
                    discountPercent: discountBp / 100,
                  })
                }
              >
                Grant at {formatMoney(payable, entry.currency)}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page page--ops stack stack-5">
      <PageHead
        title="Vela operations"
        lede="How the product is doing, and what is waiting on you."
        actions={
          <Link className="btn" to="/">
            Back to your books
          </Link>
        }
      />

      {error && <Alert tone="error">{error}</Alert>}

      {overview && (
        <StatBand>
          <Stat
            lead
            label="Monthly revenue"
            value={
              overview.monthlyRevenue.length === 0
                ? '—'
                : overview.monthlyRevenue
                    .map((line) => formatMoney(line.amountMinor, line.currency))
                    .join('  ·  ')
            }
            // Listed per currency rather than added up. Companies are billed
            // against different price lists, and a single total would be
            // revenue in no currency at all.
            note={`${overview.payingCount} paying`}
          />
          <Stat
            label="Companies"
            value={String(overview.companies)}
            note={`${overview.newThisMonth} joined this month`}
          />
          <Stat
            label="On trial"
            value={String(overview.trialingCount)}
            note={overview.expiringSoon.length > 0 ? `${overview.expiringSoon.length} ending soon` : undefined}
            tone={overview.expiringSoon.length > 0 ? 'warn' : undefined}
          />
          <Stat
            label="Waiting on you"
            value={String(overview.openRequests)}
            tone={overview.openRequests > 0 ? 'warn' : undefined}
          />
        </StatBand>
      )}

      {overview && overview.expiringSoon.length > 0 && (
        <Panel title="Trials about to run out">
          <div className="stack stack-3">
            {overview.expiringSoon.map((entry) => (
              <div key={entry.businessId} className="row">
                <span className="strong">{entry.name}</span>
                <span className="push" />
                <Badge tone={days(entry.endsAt) <= 2 ? 'warn' : 'neutral'}>
                  {days(entry.endsAt)} {days(entry.endsAt) === 1 ? 'day' : 'days'} left
                </Badge>
              </div>
            ))}
          </div>
          <p className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 0 }}>
            When a trial ends the company moves to Free rather than stopping, so nothing breaks —
            but this is the moment a conversation is worth having.
          </p>
        </Panel>
      )}

      {overview && overview.needsAttention.length > 0 && (
        <Panel title="Payment needs attention">
          <div className="stack stack-3">
            {overview.needsAttention.map((entry) => (
              <div key={entry.businessId} className="row">
                <span className="strong">{entry.name}</span>
                <span className="push" />
                <Badge tone="warn">{entry.status === 'past_due' ? 'past due' : 'cancelled'}</Badge>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title={`Waiting on you${outstanding.length ? ` · ${outstanding.length}` : ''}`}>
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : outstanding.length === 0 ? (
          <Alert tone="info">Nothing outstanding.</Alert>
        ) : (
          <div className="stack stack-3">{outstanding.map(row)}</div>
        )}
      </Panel>

      <Panel title={`Companies · ${companyData?.companies.length ?? 0}`}>
        <div className="stack stack-3">
          {(companyData?.companies ?? []).map((company) => {
            const trialDays = company.trialEndsAt ? days(company.trialEndsAt) : null;
            return (
              <div key={company.id} className="admin-request">
                <div className="row">
                  <span className="strong">{company.name}</span>
                  <Badge tone={company.status === 'active' ? 'neutral' : 'accent'}>
                    {company.planName}
                  </Badge>
                  {company.status === 'trialing' && trialDays !== null && (
                    <Badge tone={trialDays <= 2 ? 'warn' : 'neutral'}>
                      trial · {trialDays}d
                    </Badge>
                  )}
                  {(company.status === 'past_due' || company.status === 'canceled') && (
                    <Badge tone="warn">{company.status === 'past_due' ? 'past due' : 'cancelled'}</Badge>
                  )}
                  <span className="push" />
                  <Button size="sm" onClick={() => setWritingTo(writingTo === company.id ? null : company.id)}>
                    {writingTo === company.id ? 'Cancel' : 'Write to them'}
                  </Button>
                </div>

                <p className="muted" style={{ fontSize: 'var(--text-xs)', margin: 0 }}>
                  {company.members} {company.members === 1 ? 'person' : 'people'} ·{' '}
                  {company.issuedThisMonth} issued this month ·{' '}
                  {company.payableMinor > 0
                    ? `${formatMoney(company.payableMinor, company.currency)} / mo`
                    : 'not paying'}
                  {company.discountBp > 0 ? ` · ${company.discountBp / 100}% off` : ''} · joined{' '}
                  {new Date(company.joinedAt).toLocaleDateString()}
                </p>

                {writingTo === company.id && (
                  <div className="admin-decision">
                    <TextField
                      label="Subject"
                      value={note.subject}
                      onChange={(event) => setNote((n) => ({ ...n, subject: event.target.value }))}
                      placeholder="Your trial ends on Friday"
                    />
                    <TextField
                      label="Message"
                      value={note.message}
                      onChange={(event) => setNote((n) => ({ ...n, message: event.target.value }))}
                      placeholder="Goes to the owners and admins of this company"
                    />
                    <div className="row row--end">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!note.subject.trim() || !note.message.trim()}
                        loading={notify_.isPending}
                        onClick={() =>
                          notify_.mutate({
                            businessId: company.id,
                            subject: note.subject.trim(),
                            message: note.message.trim(),
                          })
                        }
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {settled.length > 0 && (
        <Panel title="Already dealt with">
          <div className="stack stack-3">{settled.map(row)}</div>
        </Panel>
      )}
    </div>
  );
}
